// src/App.tsx
import { useEffect, useState, JSX } from 'react';
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar } from '@capacitor/status-bar';
import { SocialLogin } from '@capgo/capacitor-social-login';

import { AuthProvider, useAuth } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';

import LandingPage from './pages/Landing';
import Auth from './pages/Auth';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Verify from './pages/Verify';

import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import Imprint from './pages/legal/Imprint';

import Onboarding from './components/Onboarding/Onboarding'
import Home from './pages/Home';
import Search from './pages/Search';
import Create from './pages/Create';
import Community from './pages/Community';
import Profile from './pages/Profile';
import RecipeDetail from './pages/RecipeDetail';
import EditRecipePage from './pages/EditRecipePage';
import Notifications from './pages/Notifications';
import MealPlanner from './pages/MealPlanner';
import ShoppingListPrep from './pages/ShoppingListPrep';
import ShoppingListView from './pages/ShoppingListView';
import SettingsMenu from './pages/SettingsMenu';
import AccountSettings from './pages/AccountSettings';
import SettingsFeedback from './pages/SettingsFeedback';
import AdminFeedback from './pages/admin/Feedback';
import SettingsLayout from './pages/SettingsLayout';
import DietaryPreferences from './pages/DietaryPreferences';
import Paywall from './pages/Paywall';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import ManageSubscription from './pages/ManageSubscription';

import AdminAuth from './components/admin/AdminAuth';
import AdminLayout from './pages/admin/AdminLayout';
import UnverifiedIngredientsPage from './pages/admin/UnverifiedIngredientsPage';
import MissingConversionsPage from './pages/admin/MissingConversionsPage';
import ReviewModerationPage from './pages/admin/ReviewModerationPage';
import RecipeModerationPage from './pages/admin/RecipeModerationPage';
import ReportsPage from './pages/admin/ReportsPage';

import BottomNav from './components/NavBars/BottomNav';
import AppSplashScreen from './components/SplashScreen';
import { SplashScreen as CapacitorSplash } from '@capacitor/splash-screen';

const IOS_CLIENT_ID  = '987481031708-sm47kn5godhivvuma89f40gcg3q5jd7i.apps.googleusercontent.com';
const WEB_CLIENT_ID  = '987481031708-5f8f5fu1m55g8pbogl7ijatq2p9g71ma.apps.googleusercontent.com';
// const ANDROID_CLIENT_ID = '987481031708-46rpqpe4gont9tmn1ktb4nkd8rhj2701.apps.googleusercontent.com';
const GRANT_OFFLINE  = false;

function AppWrapper() {
  // block UI until Capgo providers are registered
  const [socialReady, setSocialReady] = useState(false);

  useEffect(() => {
    // Expose platform to CSS (ios | android | web)
    try {
      const p = Capacitor.getPlatform();
      document.documentElement.dataset.platform = p;
    } catch {}
    // 1) Hide the native splash immediately
    CapacitorSplash.hide({ fadeOutDuration: 200 });
    
    // 1.5) On Android, don't overlay the WebView under the status bar
    if (Capacitor.getPlatform() === 'android') {
      StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
    }
    
    // 2) Initialize providers and WAIT for it before showing UI
    (async () => {
      try {
        console.log('[SocialLogin] init start');
        const isAndroid = Capacitor.getPlatform() === 'android';

        // Only Google on Android; Google + Apple on iOS
        const baseGoogle = {
          webClientId: WEB_CLIENT_ID,
          iOSClientId: IOS_CLIENT_ID,
          mode: GRANT_OFFLINE ? 'offline' : 'online',
        };

        const config = isAndroid
          ? { google: baseGoogle }
          : { google: baseGoogle, apple: {} };

        await SocialLogin.initialize(config as any);
        console.log('[SocialLogin] init done');
        setSocialReady(true);
      } catch (err: any) {
        console.error('[SocialLogin] init error:', err);
        // Fallback: retry with ONLY Google (handles "apple.android.redirectUrl" errors)
        try {
          console.warn('[SocialLogin] retrying init with google-only');
          await SocialLogin.initialize({
            google: {
              webClientId: WEB_CLIENT_ID,
              iOSClientId: IOS_CLIENT_ID,
              mode: GRANT_OFFLINE ? 'offline' : 'online',
            },
          } as any);
          console.log('[SocialLogin] init (google-only) done');
          setSocialReady(true);
        } catch (e) {
          console.error('[SocialLogin] fallback init failed:', e);
          setSocialReady(true); // don't brick the UI
        }
      }
    })();

    // Route every panpals://verify?customToken=XXX  or  ?token=YYY
    // into our Verify page, where we will handle sign-in or confirmation.
    const globalDeepLink = (data: any) => {
      console.log('[Global] appUrlOpen', data);
      const url = new URL(data.url);
      if (url.pathname !== '/verify') return;

      const customToken = url.searchParams.get('customToken');
      const token       = url.searchParams.get('token');

      if (customToken && customToken !== 'null') {
        window.location.hash = `/verify?customToken=${customToken}`;
      }
      else if (token) {
        window.location.hash = `/verify?token=${token}`;
      }
    };
    App.addListener('appUrlOpen', globalDeepLink);
    return () => {
      App.removeAllListeners();
    };
  }, []);

  // Wait for social login to be registered natively; otherwise the
  // plugin will say "Cannot find provider 'google'".
  if (!socialReady) {
    return <AppSplashScreen />;
  }

  return (
    <AuthProvider>
      <Router>
        <MainApp />
      </Router>
    </AuthProvider>
  );
}

function VerifiedRoute({ children }: { children: JSX.Element }) {
  const { backendUser } = useAuth();
  const location = useLocation();

  // Allow /verify to process deep links without immediate redirect
  if (backendUser && !backendUser.isEmailVerified && location.pathname !== '/verify') {
    return <Navigate to="/verify" replace />;
  }
  if (
    backendUser &&
    backendUser.isEmailVerified &&
    !backendUser.onboardingCompleted &&
    !location.pathname.startsWith('/app/onboarding')
  ) {
    return <Navigate to="/app/onboarding" replace />;
  }
  return children;
}

/**
 * Wraps the entire /app section:
 * 1) Shows splash first
 * 2) Then runs auth/verification and AppLayout
 */
function AppSectionWrapper() {
  const { loading } = useAuth();
  const location = useLocation();
  const [showSplash, setShowSplash] = useState(location.pathname.startsWith('/app'));
  console.log('[AppSectionWrapper] pathname=', location.pathname, 'initial showSplash=', showSplash);

  useEffect(() => {
    console.log('[AppSectionWrapper] location changed →', location.pathname);
    if (location.pathname.startsWith('/app')) {
      console.log('[AppSectionWrapper] showing splash for 1.5s');
      const timer = setTimeout(() => {
        console.log('[AppSectionWrapper] splash timeout complete');
        setShowSplash(false);
      }, 1500);
      return () => {
        console.log('[AppSectionWrapper] clearing splash timer');
        clearTimeout(timer);
      };
    } else {
      console.log('[AppSectionWrapper] leaving /app, hide splash immediately');
      setShowSplash(false);
    }
  }, [location.pathname]);

   // Hold the splash until BOTH timer is up and auth has initialized
   if (showSplash || loading) {
     console.log(
       '[AppSectionWrapper] rendering <AppSplashScreen />',
       'showSplash=', showSplash,
       'loading=', loading
     );
     return <AppSplashScreen />;
   }
  console.log('[AppSectionWrapper] splash & loading cleared, rendering <PrivateRoute>');
  return (
    <PrivateRoute>
      <VerifiedRoute>
        <AppLayout />
      </VerifiedRoute>
    </PrivateRoute>
  );
}

/**
 * AppLayout wraps all /app/* routes:
 * - initializes Firebase & push once
 * - renders current page via <Outlet />
 * - shows BottomNav only when backendUser.isEmailVerified
 */
function AppLayout() {
  const { user, backendUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleAppFocus = () => {
      console.log('[AppLayout] App focused, backendUser:', backendUser, 'pathname:', location.pathname);
      if (backendUser && !backendUser.isEmailVerified && location.pathname !== '/verify') {
        navigate('/verify');
      } else if (backendUser && backendUser.isEmailVerified && !backendUser.onboardingCompleted) {
        navigate('/app/onboarding');
      }
    };
    window.addEventListener('focus', handleAppFocus);
    return () => window.removeEventListener('focus', handleAppFocus);
  }, [backendUser, navigate, location.pathname]);

  // // Firebase + push-token init
  // useEffect(() => {
  //   if (!user) return;

  //   setupFirebaseListeners();

  //   (async () => {
  //     const token = await requestPushPermission();
  //     if (token) {
  //       await fetchWithAuth('/api/notifications/token', {
  //         method: 'POST',
  //         headers: { 'Content-Type': 'application/json' },
  //         body: JSON.stringify({ token }),
  //       });
  //     }
  //   })().catch(console.error);

  //   onForegroundMessage((payload) => {
  //     console.log('In-app push received:', payload);
  //   });
  // }, [user]);

  // Determine active bottom-nav tab
  const currentTab = (() => {
    const p = location.pathname;
    if (p.startsWith('/app/home')) return 'home';
    if (p.startsWith('/app/search')) return 'search';
    if (p.startsWith('/app/community')) return 'community';
    if (p.startsWith('/app/shopping-list')) return 'shoppinglist';
    if (p.startsWith('/app/meal-plan')) return 'mealplan';
    if (p.startsWith('/app/profile')) return 'profile';
    return '';
  })();

  console.log('[AppLayout] user=', user, 'backendUser=', backendUser, 'path=', location.pathname, 'currentTab=', currentTab);

  const isRecipePage = location.pathname.startsWith('/app/recipe/')

  return (
    <>
      {/* top safe-area filler on BOTH platforms (driven by --safe-top) */}
      {!isRecipePage && (
        <div
          className="fixed inset-x-0 top-0 bg-white z-50 pointer-events-none"
          style={{ height: 'var(--safe-top)' }}
        />
      )}

      {/* ── main content ── */}
      <div
        style={{
          // keep content below the punch-hole on all pages
          marginTop: !isRecipePage ? 'var(--safe-top)' : undefined,
          // leave room for the bottom nav (base + device safe area)
          paddingBottom: 'calc(var(--nav-height-base) + var(--safe-bottom))',
        }}
      >
        <Outlet />
      </div>
      {backendUser?.isEmailVerified && location.pathname !== '/app/onboarding' && (
        <BottomNav currentTab={currentTab} onNavigate={navigate} />
      )}
    </>
  );
}

function MainApp() {
  console.log('🔍 App() has rendered, path =', window.location.hash);

  return (
    <Routes>
      {/* 1) Public landing & legal */}
      <Route
        path="/"
        element={
          Capacitor.getPlatform() !== 'web' ? (
            <Navigate to="/auth" replace />
          ) : (
            <LandingPage />
          )
        }
      />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/imprint" element={<Imprint />} />

      {/* 2) Auth flows */}
      <Route path="/auth/*" element={<Auth />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/auth/action" element={<ResetPassword />} />
      <Route path="/verify" element={<Verify />} />

      {/* 3) Protected app shell (with splash first) */}  
      <Route path="/app/*" element={<AppSectionWrapper />}>
        {/* nested onboarding layout */}
        <Route path="onboarding/*" element={<Onboarding />}>
          <Route index element={<div />} />
          <Route path="home"   element={<Home />} />
          <Route path="search" element={<Search />} />
          <Route path="subscribe" element={<Paywall />} />
          <Route path="meal-plan" element={<MealPlanner />} />
          <Route path="shopping-list" element={<ShoppingListPrep />} />
          <Route path="recipe/:id" element={<RecipeDetail />} />
          <Route path="settings/preferences" element={<DietaryPreferences />} />
        </Route>

        {/* after that, your real app pages */}
        <Route index element={<Navigate to="home" replace />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="home" element={<Home />} />
        <Route path="search" element={<Search />} />
        <Route path="create" element={<Create />} />
        <Route path="community" element={<Community />} />
        <Route path="profile" element={<Profile />} />
        <Route path="profile/:id" element={<Profile />} />

        <Route path="settings/*" element={<SettingsLayout />}>
          <Route index element={<SettingsMenu />} />
          <Route path="account" element={<AccountSettings />} />
          <Route path="feedback" element={<SettingsFeedback />} />
          <Route path="preferences" element={<DietaryPreferences />} />
          <Route path="subscription" element={<ManageSubscription />} />
        </Route>

        <Route path="recipe/:id" element={<RecipeDetail />} />
        <Route path="edit-recipe/:id" element={<EditRecipePage />} />
        <Route path="notifications" element={<Notifications />} />

        <Route path="meal-plan" element={<MealPlanner />} />
        <Route path="shopping-list" element={<ShoppingListPrep />} />
        <Route path="shopping-list/view" element={<ShoppingListView />} />

        <Route path="subscribe" element={<Paywall />} />
        <Route path="subscribe/success" element={<SubscriptionSuccess />} />
      </Route>

      {/* ─── Admin panel (web only) ─── */}
      <Route
        path="/admin"
        element={
          <AdminAuth>
            <AdminLayout />
          </AdminAuth>
        }
      >
        <Route index element={<UnverifiedIngredientsPage />} />
        <Route
          path="ingredients/missing-conversion"
          element={<MissingConversionsPage />}
        />
        <Route
          path="moderation/reviews"
          element={<ReviewModerationPage />}
        />
        <Route
          path="moderation/recipes"
          element={<RecipeModerationPage />}
        />
        <Route 
          path="reports" 
          element={<ReportsPage />} 
        />
        <Route 
          path="feedback" 
          element={<AdminFeedback />} 
        />
      </Route>

      {/* 4) Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default AppWrapper;
